import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { appUpdateApi } from '../api/appUpdate'
import { isApp } from '../utils/platform'
import { guessPlatform, waitForAppVersion } from '../utils/appVersion'

/**
 * 앱이 너무 오래된 버전이면 업데이트 안내 화면으로 덮는다.
 *
 * 왜 화면을 덮나
 * ------------
 * 서버 API가 바뀌면 오래된 앱은 화면이 깨진 채로 돌아간다. 사용자는 그게
 * 버전 문제인 줄 모르고 "앱이 고장났다"고 느낀다. 그래서 무엇을 해야 하는지
 * 한 화면으로 알려주고 스토어로 보낸다.
 *
 * 켜고 끄는 건 서버가 한다
 * ----------------------
 * 판단(최소 버전 비교, 기능 on/off)은 전부 서버 설정이다. 잘못 켜면 사용자가
 * 앱을 아예 못 쓰므로, 배포 없이 즉시 되돌릴 수 있어야 한다.
 *
 * 확인에 실패하면 통과시킨다
 * ------------------------
 * 서버가 잠깐 안 되거나 응답이 이상하면 아무것도 하지 않는다. 확인이 안 된다는
 * 이유로 앱을 못 쓰게 만드는 건 원래 막으려던 문제보다 나쁘다.
 *
 * 관리자 페이지는 덮지 않는다
 * -------------------------
 * 이 게이트는 라우터 전체를 감싸고 있어서 /admin 까지 같이 덮였다. 관리자
 * 화면은 앱 버전과 아무 상관이 없는 운영 도구인데, 앱에서 열면 업데이트
 * 안내에 갇혀 손도 못 댄다. 고장 났을 때 들어가 봐야 하는 곳이 고장과 함께
 * 막히는 셈이라 더 나쁘다.
 */
const UpdateRequiredGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [blocked, setBlocked] = useState(false)
  const [storeUrl, setStoreUrl] = useState<string | null>(null)
  const [storeWebUrl, setStoreWebUrl] = useState<string | null>(null)

  const isAdmin = useLocation().pathname.startsWith('/admin')

  useEffect(() => {
    if (!isApp() || isAdmin) return

    let alive = true
    void (async () => {
      // 브리지가 버전을 넣어줄 때까지 기다린다. 안 기다리면 정상 버전인데도
      // '버전 모름'으로 잡힌다.
      const info = await waitForAppVersion()
      if (!alive) return

      const platform = info.platform ?? guessPlatform()
      const res = await appUpdateApi.check(platform, info.version)
      if (!alive || !res?.update_required) return

      setStoreUrl(res.store_url ?? null)
      setStoreWebUrl(res.store_web_url ?? null)
      setBlocked(true)
    })()

    return () => {
      alive = false
    }
  }, [isAdmin])

  if (!blocked || isAdmin) return <>{children}</>

  const openStore = () => {
    // itms-apps:// · market:// 은 웹뷰가 가로채 외부 앱(스토어)으로 연다
    // (webview_page.dart의 커스텀 스킴 처리).
    if (storeUrl) {
      window.location.href = storeUrl
      // 스토어 앱이 없는 환경(시뮬레이터·에뮬레이터)에서는 아무 일도 안 일어난다.
      // 그때를 위해 잠시 뒤 웹 주소로 한 번 더 시도한다.
      if (storeWebUrl) {
        setTimeout(() => {
          window.location.href = storeWebUrl
        }, 1200)
      }
      return
    }
    if (storeWebUrl) window.location.href = storeWebUrl
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-backgroundColorBg2 flex flex-col items-center justify-center px-32px">
      <p className="font-pretendard font-bold text-20px leading-140% text-brown-80%-main text-center">
        새로운 버전이 나왔어요
      </p>
      <p className="mt-12px font-pretendard font-regular text-15px leading-150% text-textColorText2 text-center">
        계속 이용하려면 업데이트가 필요합니다
      </p>

      <button
        type="button"
        onClick={openStore}
        className="mt-32px w-full max-w-[320px] h-52px rounded-12px bg-brown-80%-main font-pretendard font-bold text-16px text-white"
      >
        업데이트하기
      </button>
    </div>
  )
}

export default UpdateRequiredGate
